import React from "react";
import { Shield, ShieldCheck } from "lucide-react";
import { useFinancePin } from "@/context/FinancePinContext";

/**
 * FinancePinShield — discreet shield icon used as the ONLY entry point for
 * the PIN economic lock/unlock flow on Dashboard and AI Manager.
 *
 * Placement is up to the caller: it's typically dropped in the top-right
 * corner of a KPI section header. The component stays out of the way:
 *   • Renders nothing when the feature isn't enabled (no visual noise for
 *     free / Pro users who never set up a PIN).
 *   • Locked state → outlined Shield (champagne-tinted, low-contrast).
 *     Click → opens the PIN modal.
 *   • Unlocked state → filled ShieldCheck. Click → instant relock (no
 *     confirmation, no toast — the operator clicked it on purpose).
 *   • Backend-lockout state → same outlined Shield but a `disabled` cursor
 *     and tooltip pointing the user to retry later. The modal still opens
 *     so the user sees the "Riprova fra X minuti" hint inside.
 *
 * Accessibility: it's a real <button>, focusable, with role + title that
 * adapt to state. The icon is decorative.
 */
export default function FinancePinShield({ className = "" }) {
  const { enabled, unlocked, locked, openPinModal, relock } = useFinancePin();
  if (!enabled) return null;

  const isUnlocked = unlocked;
  const Icon = isUnlocked ? ShieldCheck : Shield;
  const handleClick = () => {
    if (isUnlocked) {
      relock();
    } else {
      openPinModal();
    }
  };

  // Tooltips intentionally short — the receptionist threat model is
  // "doesn't notice it", not "doesn't understand it".
  let title;
  if (isUnlocked) title = "Clicca per nascondere";
  else if (locked) title = "Area protetta — riprova fra qualche minuto";
  else title = "Area protetta";

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title}
      aria-label={title}
      data-testid="finance-pin-shield"
      data-locked={isUnlocked ? "false" : "true"}
      className={`
        inline-flex items-center gap-1.5
        px-2.5 h-9 rounded-full
        text-xs font-medium tracking-wide
        border transition-all duration-200
        cursor-pointer select-none
        ${isUnlocked
          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
          : "bg-primary/5 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"}
        focus:outline-none focus:ring-2 focus:ring-primary/30
        ${className}
      `.replace(/\s+/g, " ").trim()}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="hidden sm:inline">
        {isUnlocked ? "Nascondi" : "Sblocca"}
      </span>
    </button>
  );
}
